import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  TextField,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

interface TenantSwitchOption {
  id: string;
  name: string;
}

export default function TenantSwitcher() {
  const { t } = useTranslation('common');
  const { isSuperuser, currentTenant, searchTenants, switchTenant } = useAuth();
  const [options, setOptions] = useState<TenantSwitchOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const activeRequestRef = useRef(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const loadTenants = useCallback(async (search: string, limit = 30) => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;
    setLoading(true);
    try {
      const found = await searchTenants(search, limit);
      if (activeRequestRef.current !== requestId) {
        return;
      }
      const tenantOptions: TenantSwitchOption[] = found.map((tenant) => ({
        id: tenant.id,
        name: tenant.name,
      }));
      setOptions(tenantOptions);
    } finally {
      if (activeRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [searchTenants]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void loadTenants(inputValue.trim());
    }, 350);

    return () => {
      window.clearTimeout(timer);
    };
  }, [inputValue, loadTenants, open]);

  useEffect(() => {
    if (!currentTenant?.id) {
      return;
    }
    setOptions((prev) => {
      if (prev.some((option) => option.id === currentTenant.id)) {
        return prev;
      }
      return [{ id: currentTenant.id, name: currentTenant.name }, ...prev];
    });
  }, [currentTenant?.id, currentTenant?.name]);

  const selectedOption = useMemo<TenantSwitchOption | null>(() => {
    if (!currentTenant?.id) {
      return null;
    }
    const cached = options.find((option) => option.id === currentTenant.id);
    if (cached) {
      return cached;
    }
    return {
      id: currentTenant.id,
      name: currentTenant.name,
    };
  }, [currentTenant?.id, currentTenant?.name, options]);

  if (!isSuperuser) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: isMobile ? '100%' : 'auto',
      }}
    >
      <Business
        sx={{
          color: 'text.secondary',
          fontSize: 20,
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          width: isMobile ? '100%' : 300,
          minWidth: 0,
        }}
      >
        <Autocomplete<TenantSwitchOption, false, true, false>
          options={options}
          open={open}
          onOpen={() => {
            setOpen(true);
            void loadTenants(inputValue.trim(), 30);
          }}
          onClose={() => {
            setOpen(false);
            setInputValue('');
          }}
          value={selectedOption ?? undefined}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setInputValue(newInputValue);
            }
          }}
          onChange={(_event, option) => {
            if (!option?.id) {
              return;
            }
            if (option.id === currentTenant?.id) {
              return;
            }
            switchTenant(option.id, option.name);
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loading}
          disableClearable
          clearOnBlur={false}
          loadingText={t('common.loading')}
          noOptionsText={t('tenantSwitcher.noResults')}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option.name}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('tenantSwitcher.label')}
              placeholder={t('tenantSwitcher.placeholder')}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          size="small"
          sx={{
            width: '100%',
          }}
        />
      </Box>
    </Box>
  );
}
